import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { BlogSidebar } from '@/components/blog/BlogSidebar'
import { resolvePublishedDate } from '@/lib/blogDates'
import { dedupePosts, postHasImage } from '@/lib/blogPosts'
import {
  blogListSchema,
  canonicalPathFor,
  categoryPathFor,
  fetchCategories,
  fetchPages,
  SITE_ORIGIN,
  type BlogCategory,
  type BlogPost,
} from '@/lib/blogApi'

/**
 * The blog listing — /blog and /blog/category/:category.
 *
 * Ported from NevTan Sign's Blog screen (docusignUi's src/screens/Blog/index.jsx), rebuilt as
 * Tailwind JSX and as a pure client-side fetch (this app has no server render to seed it with
 * initial data the way Sign's Next.js route shell does).
 */

const GRID_STEP = 6

const FALLBACK_INTRO =
  'Guides on running local AI models privately, choosing hardware, and getting the most out of NevTan AI.'

function thumbOf(p: BlogPost): string | null {
  return p?.heroImageUrl || p?.thumbnailUrl || null
}

function formatDate(value?: string | null): string {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * The category picker that sits above the post grid.
 *
 * Built from the API's category list, which only contains categories that already have a live
 * post — so a section nobody has published into is never offered, and every option leads
 * somewhere with content in it. The options are real links to /blog/category/{slug} rather than
 * client-side filtering, so a category is a shareable, indexable page that survives a refresh.
 */
function CategoryPicker({
  categories,
  activeCategory,
}: {
  categories: BlogCategory[]
  activeCategory: BlogCategory | null
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  if (!categories.length) return null

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex items-center gap-2 rounded-lg border border-brand-line-strong bg-brand-paper px-3.5 py-2 text-[13.5px] font-bold text-brand-heading transition hover:border-brand"
      >
        <span className={activeCategory ? '' : 'text-brand-muted'}>
          {activeCategory ? activeCategory.name : 'Select a category'}
        </span>
        <ChevronDown size={15} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full z-20 mt-1.5 w-[230px] overflow-hidden rounded-xl border border-brand-line bg-brand-paper p-1.5 shadow-float"
        >
          <Link
            to="/blog"
            onClick={() => setOpen(false)}
            className={`block rounded-lg px-3 py-2 text-[13.5px] font-semibold transition ${
              !activeCategory ? 'bg-brand-tint/60 text-brand-tint-ink' : 'text-brand-body hover:bg-brand-tint/40'
            }`}
          >
            All posts
          </Link>
          {categories.map((category) => (
            <Link
              key={category.slug}
              to={categoryPathFor(category)}
              onClick={() => setOpen(false)}
              className={`block rounded-lg px-3 py-2 text-[13.5px] font-semibold transition ${
                activeCategory?.slug === category.slug
                  ? 'bg-brand-tint/60 text-brand-tint-ink'
                  : 'text-brand-body hover:bg-brand-tint/40'
              }`}
            >
              {category.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export function BlogPage() {
  const { category: categorySlug } = useParams<{ category?: string }>()

  const [allPages, setAllPages] = useState<BlogPost[]>([])
  const [pages, setPages] = useState<BlogPost[]>([])
  const [categories, setCategories] = useState<BlogCategory[]>([])
  const [categoriesLoaded, setCategoriesLoaded] = useState(false)
  const [shown, setShown] = useState(GRID_STEP)

  // The unfiltered list, for the featured article and the sidebar.
  useEffect(() => {
    let cancelled = false
    fetchPages().then((data) => {
      if (!cancelled) setAllPages(data)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // The grid, narrowed to the active category (or everything, on /blog).
  useEffect(() => {
    let cancelled = false
    fetchPages({ category: categorySlug ?? null }).then((data) => {
      if (!cancelled) setPages(data)
    })
    return () => {
      cancelled = true
    }
  }, [categorySlug])

  // The category nav, fetched separately so it can fail (or arrive) on its own.
  useEffect(() => {
    let cancelled = false
    fetchCategories().then((data) => {
      if (!cancelled) {
        setCategories(data)
        setCategoriesLoaded(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  // A fresh category should start back at the top of the grid.
  useEffect(() => {
    setShown(GRID_STEP)
  }, [categorySlug])

  // Only claim "not found" once the category list has actually loaded and has entries — an
  // empty list just means the API hasn't returned yet (or `nevtan_ai` has no posts at all),
  // never that a real category slug is wrong.
  const activeCategory = categorySlug
    ? categories.find((c) => c.slug === categorySlug) ?? null
    : null
  const categoryNotFound = !!categorySlug && categoriesLoaded && categories.length > 0 && !activeCategory

  const order = (list: BlogPost[]) => {
    const deduped = dedupePosts(list)
    const byNewest = (a: BlogPost, b: BlogPost) =>
      new Date(resolvePublishedDate(b, list) || 0).getTime() -
      new Date(resolvePublishedDate(a, list) || 0).getTime()
    return [
      ...deduped.filter(postHasImage).sort(byNewest),
      ...deduped.filter((p) => !postHasImage(p)).sort(byNewest),
    ]
  }

  // The whole catalogue drives the hero and the sidebar; the category only drives the grid.
  const everything = useMemo(() => order(allPages), [allPages])
  const inCategory = useMemo(() => order(pages), [pages])

  // Feature the newest post that has an image, from the full catalogue — so the hero is the
  // same article on every page of the blog rather than shifting with the category.
  const featured = everything[0] || null

  // On /blog the hero already showed it, so the grid is everything else. Inside a category the
  // grid is the category — the whole of it, since it is the answer to what was just asked for.
  const rest = activeCategory ? inCategory : inCategory.filter((p) => p.slug !== featured?.slug)

  const visibleRest = rest.slice(0, shown)
  const hasMore = shown < rest.length

  const heading = activeCategory ? activeCategory.name : 'NevTan AI Blog'
  const intro =
    activeCategory?.description ||
    (activeCategory
      ? `Articles on ${activeCategory.name} from the NevTan AI team.`
      : FALLBACK_INTRO)
  const path = categorySlug ? categoryPathFor(categorySlug) : '/blog'

  useDocumentHead({
    title: activeCategory ? `${heading} | NevTan AI Blog` : 'NevTan AI Blog',
    description: intro,
    canonical: `${SITE_ORIGIN}${path}`,
    jsonLd: categoryNotFound
      ? undefined
      : blogListSchema(activeCategory ? pages : everything, { path, name: heading, description: intro }),
  })

  if (categoryNotFound) {
    return (
      <div className="nv-container py-20 text-center sm:py-28">
        <h1 className="nv-h2 text-[1.6rem] sm:text-[2rem]">Category not found</h1>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-brand-body">
          This category doesn&apos;t exist, or hasn&apos;t been published into yet.
        </p>
        <Link to="/blog" className="nv-btn-primary-lg mt-7 inline-flex">
          Back to Blog <ArrowRight size={16} />
        </Link>
      </div>
    )
  }

  return (
    <div className="nv-container py-10 sm:py-14">
      {activeCategory && (
        <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-[13px] text-brand-muted">
          <Link to="/" className="hover:text-brand">
            Home
          </Link>
          <span>›</span>
          <Link to="/blog" className="hover:text-brand">
            Blog
          </Link>
          <span>›</span>
          <span className="text-brand-heading">{activeCategory.name}</span>
        </nav>
      )}

      <div className="max-w-2xl">
        <h1 className="nv-h1 text-[1.9rem] leading-tight sm:text-[2.3rem]">{heading}</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-brand-body">{intro}</p>
      </div>

      <div className="mt-9 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
        {/* Main column */}
        <div className="min-w-0">
          {everything.length === 0 && (
            <p className="rounded-xl border border-dashed border-brand-line bg-brand-panel px-5 py-8 text-center text-[14px] text-brand-muted">
              No articles yet — check back soon.
            </p>
          )}

          {/* Featured post */}
          {featured && (
            <Link
              to={canonicalPathFor(featured)}
              className="group grid grid-cols-1 overflow-hidden rounded-xl border border-brand-line bg-brand-paper shadow-card transition hover:shadow-raised sm:grid-cols-2"
            >
              <span className="block aspect-video overflow-hidden bg-brand-tint sm:aspect-auto sm:h-full">
                {thumbOf(featured) && (
                  <img
                    src={thumbOf(featured) as string}
                    alt={featured.title}
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                  />
                )}
              </span>
              <span className="flex flex-col justify-center p-6">
                <h2 className="text-[19px] font-extrabold leading-snug text-brand-heading">
                  {featured.title}
                </h2>
                <span className="mt-2 flex items-center gap-2 text-[12.5px] text-brand-muted">
                  <span>{featured.author?.name || 'NevTan AI Team'}</span>
                  <span>· {formatDate(resolvePublishedDate(featured, allPages))}</span>
                </span>
                <p className="mt-3 line-clamp-3 text-[13.5px] leading-relaxed text-brand-body">
                  {featured.metaDescription}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-[13.5px] font-bold text-brand">
                  Read Article <ArrowRight size={15} />
                </span>
              </span>
            </Link>
          )}

          {(rest.length > 0 || categories.length > 0) && (
            <div className="mt-10 flex items-center justify-between gap-3">
              <h2 className="nv-h2 text-[1.3rem]">
                {activeCategory ? (
                  <>{activeCategory.name} Posts</>
                ) : (
                  <>
                    <span className="text-brand">Latest</span> Posts
                  </>
                )}
              </h2>
              <CategoryPicker categories={categories} activeCategory={activeCategory} />
            </div>
          )}

          {rest.length > 0 && (
            <>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {visibleRest.map((p) => {
                  const t = thumbOf(p)
                  return (
                    <Link
                      key={p.slug}
                      to={canonicalPathFor(p)}
                      className="group overflow-hidden rounded-xl border border-brand-line bg-brand-paper shadow-card transition hover:-translate-y-0.5 hover:shadow-raised"
                    >
                      <span className="block aspect-video overflow-hidden bg-brand-tint">
                        {t ? (
                          <img
                            src={t}
                            alt={p.title}
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                          />
                        ) : null}
                      </span>
                      <span className="block p-4">
                        <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-brand">
                          {p.pageType || 'Article'}
                        </span>
                        <span className="mt-1.5 block text-[15px] font-extrabold leading-snug text-brand-heading">
                          {p.title}
                        </span>
                        <span className="mt-1.5 line-clamp-2 block text-[13px] leading-relaxed text-brand-muted">
                          {p.metaDescription}
                        </span>
                        <span className="mt-3 flex items-center justify-between border-t border-brand-line pt-2.5 text-[12px] text-brand-muted">
                          <span>{formatDate(resolvePublishedDate(p, allPages))}</span>
                          <span className="font-bold text-brand">Read →</span>
                        </span>
                      </span>
                    </Link>
                  )
                })}
              </div>
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setShown((n) => n + GRID_STEP)}
                  className="nv-btn-outline-lg mt-8"
                >
                  See More
                </button>
              )}
            </>
          )}
        </div>

        {/* Sidebar — always the full catalogue, unaffected by the active category. */}
        <BlogSidebar pages={everything} />
      </div>
    </div>
  )
}
